// ============================================================================
// Lifespan v1 — simulation engine (typed TS port of the M0 reference
// sim/engine.js). Math is reproduced operation-for-operation: this file must
// stay BIT-IDENTICAL to the frozen reference (sim/golden/engine.golden.cjs),
// which the golden-master test enforces (sim/test/golden.test.ts).
//
// Implements GDD.md formulas exactly (v1.3.0 — the smoothed burnout + wellM bands):
//   Income   (§4.2): income = wages[J] * inten * (study?0.75) * capacity(M) * (H<25?0.60)
//                    capacity(M) = 1 - 0.25*clamp((burnoutHi-M)/(burnoutHi-burnoutLo), 0, 1)
//   Living   (§5):   col = 12000 + 0.10*income ; F = income - col - s
//   Savings  (§6):   S <- S*e^(r*dt) + (F/r)*(e^(r*dt)-1), r = 0.03 (S>=0) / 0.08 (S<0),
//                    split at sign crossings; Living (incl. tuition) locks to $0 below
//                    -25k and unlocks (hysteresis) only above -20k
//   Morale   (§6M):  M* = clamp(100 - 5*(D - G), 0, 100); M <- M* + (M-M*)*e^(-0.20*dt)
//                    D = drain[mode] + (study?5:0); G = 15*(1 - e^(-s/(0.15*wage)))
//   Skills   (§7):   g(M) = clamp((M-30)/50, 0, 1); studying: p += ∫g(M(t))dt (analytic);
//                    idle: p *= e^(-0.288*dt); complete at p >= studyYears[K+1]
//   Health   (§8):   dH/dt = -(0.25 + 0.0009*(age-18)^2) - 0.75*[ot] - 0.5*burnFrac(M)
//                    + 0.3*wellFrac(M) + max(0, 4 - 0.1*(age-18))*[sab];  H<=100; death at H<=0
//                    wellFrac(M) = clamp((M-wellLo)/(wellHi-wellLo), 0, 1)  (v1.3.0 band, was [M>=70])
//
// Integration contract (§10): cash & morale use closed forms exactly; skill &
// health integrate in fixed 1/12-game-year substeps of ABSOLUTE calendar time
// (substep n covers ages [18 + n/12, 18 + (n+1)/12)); M-dependent flags
// (burnout, flourishing, frailty, broke-lock) are frozen at each substep start.
// settleTo(state, targetAge) processes only whole substeps, so ANY settlement
// pattern reproduces the identical substep sequence bit-for-bit (G7).
// ============================================================================

import type {
  CardEvent,
  CardPolicy,
  LifeResult,
  Mode,
  Params,
  PlanAction,
  State,
  Strategy,
  SimOpts,
  YearRecord,
} from './types.ts';

export const clamp = (x: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, x));

export function initState(P: Params): State {
  return {
    n: 0, // completed substeps since startAge (integer)
    S: P.S0,
    M: P.M0,
    H: P.H0,
    K: 0, // highest completed skill
    J: 0, // current job tier (promotion at annual review)
    p: 0, // partial study progress (study-years)
    studying: false,
    enrolledSkill: 0, // skill tuition has been paid for
    sabYearsSinceSkill: 0, // cumulative sabbatical TIME since last skill completion
    brokeLock: false, // Living locked to $0 (S fell below debtFloor)
    alive: true,
    deathAge: null,
    policy: { mode: 'std', livingFrac: 0 },
    // --- accounting (identity §6) & profiling ---
    interestAccum: 0,
    earnedAccum: 0,
    colAccum: 0,
    livingAccum: 0,
    tuitionAccum: 0,
    cardCashAccum: 0,
    moraleInt: 0,
    flourishYears: 0,
    minM: P.M0,
    minS: P.S0,
    skillAges: [], // age at each skill completion
  };
}

export const ageOf = (st: State, P: Params): number => P.startAge + st.n * P.substep;

// --- cash: closed form with sign-crossing split ------------------------------
export function cashAdvance(
  S: number,
  F: number,
  h: number,
  P: Params,
): { S: number; interest: number } {
  let interest = 0;
  let rem = h;
  let guard = 0;
  while (rem > 1e-15 && guard++ < 8) {
    const r = S > 0 || (S === 0 && F >= 0) ? P.r : P.rDebt;
    // crossing time: S(t) = (S + F/r)e^{rt} - F/r = 0  =>  e^{rt} = F/(rS+F)
    let tc = Infinity;
    const denom = r * S + F;
    if (denom !== 0) {
      const ratio = F / denom;
      if (ratio > 1 + 1e-14) tc = Math.log(ratio) / r;
    }
    const seg = Math.min(rem, tc);
    let S2: number;
    if (tc <= rem) {
      S2 = 0; // land exactly on the crossing
    } else {
      const e = Math.exp(r * seg);
      S2 = S * e + (F / r) * (e - 1);
    }
    interest += S2 - S - F * seg;
    S = S2;
    rem -= seg;
  }
  return { S, interest };
}

// --- morale helpers ----------------------------------------------------------
export const moraleAt = (M0: number, Ms: number, k: number, t: number): number =>
  Ms + (M0 - Ms) * Math.exp(-k * t);

// time at which M(t) = X (exponential approach M0 -> Ms), or null
export function crossTime(M0: number, Ms: number, k: number, X: number): number | null {
  const a = M0 - Ms,
    b = X - Ms;
  if (a * b <= 0) return null; // X not between M0 and Ms (same side needed)
  const ratio = a / b;
  if (ratio <= 1) return null; // already past X
  return Math.log(ratio) / k;
}

// ∫0..h g(M(t)) dt with g = clamp((M-lo)/span, 0, 1), M(t) exponential. Exact.
export function gIntegral(
  M0: number,
  Ms: number,
  k: number,
  h: number,
  lo: number,
  span: number,
): number {
  const hi = lo + span;
  const ts = [0, h];
  for (const X of [lo, hi]) {
    const t = crossTime(M0, Ms, k, X);
    if (t !== null && t > 0 && t < h) ts.push(t);
  }
  ts.sort((a, b) => a - b);
  let sum = 0;
  for (let i = 0; i + 1 < ts.length; i++) {
    const t1 = ts[i]!,
      t2 = ts[i + 1]!;
    if (t2 - t1 < 1e-15) continue;
    const mid = moraleAt(M0, Ms, k, (t1 + t2) / 2);
    if (mid >= hi) sum += t2 - t1;
    else if (mid > lo) {
      sum +=
        ((Ms - lo) * (t2 - t1) +
          ((M0 - Ms) / k) * (Math.exp(-k * t1) - Math.exp(-k * t2))) /
        span;
    }
  }
  return sum;
}

// --- burnout capacity (v1.2.0: the smoothed step) ----------------------------
// Capacity loss as a fraction in [0,1]: 0 at M >= burnoutHi, 1 at M <= burnoutLo,
// linear between. Replaces the discontinuous M < 20 step — a rate that jumps at
// an observable threshold is surfable by reacting to morale (checking in wins),
// which philosophy #1 forbids. What linearity buys (the correct, measured
// invariant — G14): observing M adds ~nothing over the best PRE-COMMITTED
// calendar plan at the same granularity (saw-surfer $1.898M vs Bresenham-
// dithered duty $1.898M; residual reactive edge ~0.1%). Note the stronger claim
// "oscillating across the band gains nothing over the steady average" holds
// only at FIXED mode: when mode covaries with M (every surfer), income =
// modeMult x capacity(M) has a covariance term, and fine-grained mode blends
// legitimately beat coarse yearly ones — sanctioned §4.2 strategy space,
// bounded by G13, not a reactive edge.
export function burnoutFrac(M: number, P: Params): number {
  return clamp((P.burnoutHi - M) / (P.burnoutHi - P.burnoutLo), 0, 1);
}

/** Income capacity multiplier from morale: 1 → burnoutMult across the band. */
export function capacityMult(M: number, P: Params): number {
  return 1 - (1 - P.burnoutMult) * burnoutFrac(M, P);
}

// --- instantaneous health rates (§8) — the itemised erosion readout ----------
// The per-year components of dH/dt at the state's CURRENT frozen flags (the
// same terms healthDelta integrates). Exported for the M6 UI's Check-up /
// Doctor's Letter readout — aging / overwork / misery / flourishing /
// sabbatical, per year, engine-owned so the screen can never drift from the
// integral.
export interface HealthRates {
  aging: number;
  overwork: number;
  misery: number;
  flourishing: number;
  sabbatical: number;
  /** net dH/dt (positive = recovering; H is still capped at 100) */
  net: number;
}

export function healthRates(st: State, P: Params): HealthRates {
  const t0 = st.n * P.substep; // years since startAge
  const pol = normalizePolicy(st, P);
  const aging = P.hBase + P.hQuad * t0 * t0;
  const overwork = pol.mode === 'ot' ? P.hOT : 0;
  const misery = P.hBurn * burnoutFrac(st.M, P);
  const flourishing = P.wellBonus * wellFrac(st.M, P);
  const sabbatical =
    pol.mode === 'sab' ? Math.max(0, P.sabHealthBase - P.sabHealthSlope * t0) : 0;
  return {
    aging,
    overwork,
    misery,
    flourishing,
    sabbatical,
    net: -aging - overwork - misery + flourishing + sabbatical,
  };
}

// --- study speed (§7) — the g(M) ramp, exported for the M5 UI ----------------
// "studying at 95%": zero at/below gateLo, full speed at gateLo+gateSpan.
// Same clamp the analytic study integral applies per substep (gIntegral).
export function studySpeed(M: number, P: Params): number {
  return clamp((M - P.gateLo) / P.gateSpan, 0, 1);
}

// --- flourishing band (v1.3.0: the smoothed wellM step) ----------------------
// Fraction of the flourishing health dividend earned at morale M: 0 at/below
// wellLo, 1 at/above wellHi, linear between. Same rationale as burnoutFrac —
// the binary M >= 70 flag made the post-ladder Living dial a knife edge (+5.2%
// of estate across a 0.02-point dial change at the threshold); a linear band
// has no cliff to ride and no threshold to hold a morale target just above.
export function wellFrac(M: number, P: Params): number {
  return clamp((M - P.wellLo) / (P.wellHi - P.wellLo), 0, 1);
}

// --- morale equilibrium (§6M) — the engine-owned M* formula ------------------
// Exported so the UI's M*-ghost / countdowns / drain-gain decomposition read
// the same math the substeps integrate (M1 rule: no engine-math copies in the
// app).

/** Morale gain G (pt/yr) purchased by the Living dial (§5, §6M). */
export function livingGain(livingFrac: number, wage: number, P: Params): number {
  const s = livingFrac * wage;
  return s > 0 ? P.expCap * (1 - Math.exp(-s / (P.expScaleFrac * wage))) : 0;
}

export function moraleTargetOf(
  mode: Mode,
  studying: boolean,
  livingFrac: number,
  wage: number,
  P: Params,
): number {
  const D = P.drain[mode] + (studying ? P.studyDrain : 0);
  const G = livingGain(livingFrac, wage, P);
  return clamp(100 - 5 * (D - G), 0, 100);
}

/**
 * Engine-boundary policy normalization (M1 port contract), in ONE place:
 * unknown modes fall back to 'std'; livingFrac clamps to [0, livingFracMax];
 * the broke lock forces the spend fraction to $0 (§6). Consumed by doSubstep,
 * moraleTargetFor, currentIncome AND the UI read-models (M1 same-code rule:
 * the on-screen drain/gain decomposition must normalize exactly as the
 * substeps integrate — never a second copy of this chain in the app).
 */
export function normalizePolicy(st: State, P: Params): { mode: Mode; frac: number } {
  const mode = (
    (P.modeMult as Record<string, number>)[st.policy.mode] !== undefined
      ? st.policy.mode
      : 'std'
  ) as Mode;
  const frac = st.brokeLock ? 0 : clamp(st.policy.livingFrac || 0, 0, P.livingFracMax);
  return { mode, frac };
}

/** M* for the state's live policy (broke lock forces the spend term to $0). */
export function moraleTargetFor(st: State, P: Params): number {
  const { mode, frac } = normalizePolicy(st, P);
  return moraleTargetOf(mode, st.studying, frac, P.wages[st.J]!, P);
}

// --- health integrals --------------------------------------------------------
// sabbatical recovery ∫ max(0, base - slope*t) dt over [t0,t1], t = age-18
export function sabRecoveryIntegral(t0: number, t1: number, P: Params): number {
  const tEnd = P.sabHealthBase / P.sabHealthSlope; // 40 => age 58
  const a = Math.min(t0, tEnd),
    b = Math.min(t1, tEnd);
  if (b <= a) return 0;
  return P.sabHealthBase * (b - a) - (P.sabHealthSlope * (b * b - a * a)) / 2;
}

// total health delta over [t0, t0+tau] with frozen flags. burnFrac is the
// burnout-band fraction (0..1) frozen at substep start — the misery erosion
// scales continuously with it (v1.2.0), same smoothing as income capacity.
export function healthDelta(
  t0: number,
  tau: number,
  mode: string,
  burnFrac: number,
  wFrac: number,
  P: Params,
): number {
  const t1 = t0 + tau;
  let dH = -(P.hBase * tau + (P.hQuad * (t1 ** 3 - t0 ** 3)) / 3);
  if (mode === 'ot') dH -= P.hOT * tau;
  if (burnFrac > 0) dH -= P.hBurn * burnFrac * tau;
  if (wFrac > 0) dH += P.wellBonus * wFrac * tau;
  if (mode === 'sab') dH += sabRecoveryIntegral(t0, t1, P);
  return dH;
}

// --- one substep -------------------------------------------------------------
export function doSubstep(st: State, P: Params): void {
  const h = P.substep;
  const t0 = st.n * P.substep; // years since startAge at substep start
  // input validation at the engine boundary (M1 port contract): see
  // normalizePolicy — a negative livingFrac must never mint cash; an oversized
  // one must never exceed wage; the broke lock zeroes the spend fraction.
  const { mode, frac } = normalizePolicy(st, P);
  const w = P.wages[st.J]!;

  // flags frozen at substep start (burnFrac: the continuous capacity fraction)
  const burnFrac = burnoutFrac(st.M, P);
  const frail = st.H < P.frailH;
  const wFrac = wellFrac(st.M, P);
  const studying = st.studying;

  // income / flow (§4.2, §5)
  let mult = (P.modeMult as Record<string, number>)[mode]!;
  if (studying) mult *= P.studyMult;
  mult *= 1 - (1 - P.burnoutMult) * burnFrac;
  if (frail) mult *= P.frailMult;
  const income = w * mult;
  const col = P.colBase + P.colSlope * income;
  const s = frac * w;
  const F = income - col - s;

  // health first: detect death inside the substep, shorten hEff if so (§8)
  let hEff = h;
  let dying = false;
  const dHfull = healthDelta(t0, h, mode, burnFrac, wFrac, P);
  if (st.H + dHfull <= 0) {
    dying = true;
    let lo = 0,
      hi = h;
    for (let i = 0; i < 80; i++) {
      const mid = (lo + hi) / 2;
      if (st.H + healthDelta(t0, mid, mode, burnFrac, wFrac, P) > 0) lo = mid;
      else hi = mid;
    }
    hEff = hi;
  }

  // cash (§6, closed form)
  const { S: S2, interest } = cashAdvance(st.S, F, hEff, P);

  // morale (§6M, closed form)
  const D = P.drain[mode as keyof typeof P.drain] + (studying ? P.studyDrain : 0);
  const G = s > 0 ? P.expCap * (1 - Math.exp(-s / (P.expScaleFrac * w))) : 0;
  const Ms = clamp(100 - 5 * (D - G), 0, 100);
  const M2 = moraleAt(st.M, Ms, P.kM, hEff);

  // study (§7) — exact analytic integral of g over the morale exponential
  if (studying) {
    st.p += gIntegral(st.M, Ms, P.kM, hEff, P.gateLo, P.gateSpan);
  } else if (st.p > 0) {
    st.p *= Math.exp(-P.progressDecayRate * hEff);
    // paused progress decaying below epsilon cancels the enrolment (tuition
    // forfeit — the Degree Rusting fiction). Prevents a once-per-life gate
    // check: enrol at M>=70, park a sliver of progress, resume decades later.
    if (st.p < P.progressEpsilon) {
      st.p = 0;
      st.enrolledSkill = 0;
    }
  }

  // profiling (exact morale integral)
  st.moraleInt += Ms * hEff + ((st.M - Ms) * (1 - Math.exp(-P.kM * hEff))) / P.kM;
  if (wFrac > 0) st.flourishYears += wFrac * hEff; // fractional since v1.3.0

  // commit
  st.interestAccum += interest;
  st.earnedAccum += income * hEff;
  st.colAccum += col * hEff;
  st.livingAccum += s * hEff;
  st.S = S2;
  st.minS = Math.min(st.minS, st.S);
  st.M = M2;
  st.minM = Math.min(st.minM, st.M);
  st.H = dying ? 0 : Math.min(100, st.H + dHfull);
  if (mode === 'sab') st.sabYearsSinceSkill += hEff; // time-integrated gate

  // broke lock (§6): engage below debtFloor, release with hysteresis only
  // above debtRelease (no substep-to-substep flicker at the floor)
  if (st.S < P.debtFloor) st.brokeLock = true;
  else if (st.brokeLock && st.S > P.debtRelease) st.brokeLock = false;

  // skill completion (§7)
  if (studying && st.K < 6 && st.p >= P.studyYears[st.K + 1]! - 1e-12) {
    st.K += 1;
    st.p = 0;
    st.studying = false;
    st.sabYearsSinceSkill = 0;
    st.skillAges.push(P.startAge + t0 + hEff);
  }

  if (dying) {
    st.alive = false;
    st.deathAge = P.startAge + t0 + hEff;
  } else {
    st.n += 1;
  }
}

// --- lazy settle: whole substeps only; chunk-pattern invariant (G7) ----------
export function settleTo(st: State, targetAge: number, P: Params): void {
  const targetN = Math.floor((targetAge - P.startAge) / P.substep + 1e-9);
  while (st.alive && st.n < targetN) doSubstep(st, P);
}

// --- enrolment gates (§7, §12 audit) ----------------------------------------
export function canEnroll(st: State, next: number, P: Params): boolean {
  if (st.K !== next - 1 || next > 6) return false;
  if (st.brokeLock) return false; // tuition is Living (§5)
  if (P.enrollMSkills.includes(next) && st.M < P.enrollM) return false;
  if (
    P.sabRequiredForSkills.includes(next) &&
    st.sabYearsSinceSkill < P.sabMinYears - 1e-9
  )
    return false;
  return true;
}

// --- deterministic PRNG ------------------------------------------------------
export function mulberry32(a: number): () => number {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- current income at the instant of a card (mirrors doSubstep's mult chain)
export function currentIncome(st: State, P: Params): number {
  const { mode } = normalizePolicy(st, P);
  let mult = (P.modeMult as Record<string, number>)[mode]!;
  if (st.studying) mult *= P.studyMult;
  mult *= 1 - (1 - P.burnoutMult) * burnoutFrac(st.M, P);
  if (st.H < P.frailH) mult *= P.frailMult;
  return P.wages[st.J]! * mult;
}

// --- micro-card deck (§8E.1) — seeded, policy-independent --------------------
// Card generation depends only on (seed, year, index) so two bots on identical
// policy see the identical deck. Cash is a FRACTION OF CURRENT INCOME, resolved
// at accept time and clamped at microCashCapFrac (the GDD's "<=2% of annual
// income" cap holds under every work mode). Morale deltas are deliberately
// small (0.2–0.5 pt): even accepting every spend card cannot lift the OT+study
// equilibrium past gateLo. Cash is priced PER MORALE POINT: spend cards charge
// roughly the dial's marginal price of morale (EV ~ 0), earn cards pay a poor
// gig rate (bounding the sell-morale-at-the-cap harvest inside G10's budget).
// Default of every card = status quo (decline).
export function cardsForYear(year: number, seed: number, P: Params): CardEvent[] {
  const rng = mulberry32((seed * 7919 + year * 104729) | 0);
  const events: CardEvent[] = [];
  for (let i = 0; i < P.microCardsPerYear; i++) {
    const t = (i + 0.1 + 0.8 * rng()) / P.microCardsPerYear; // within-year time
    const type = rng() < 0.5 ? 'spend' : 'earn';
    const morale =
      P.microMoraleMin + (P.microMoraleCap - P.microMoraleMin) * rng(); // 0.2..0.5 pt
    const perPt =
      type === 'spend'
        ? P.microSpendPerPtLo + (P.microSpendPerPtHi - P.microSpendPerPtLo) * rng()
        : P.microEarnPerPtLo + (P.microEarnPerPtHi - P.microEarnPerPtLo) * rng();
    const cashFrac = morale * perPt; // of current income
    events.push({ t, type, cashFrac, morale });
  }
  events.sort((a, b) => a.t - b.t);
  return events;
}

// Card policies (the G10 family — 'default' must lose nothing to any of them):
//   'default'     decline everything (status quo — identical to never logging in)
//   'greedy'      accept every earn card (cash-positive), decline spend
//   'spendthrift' accept every spend card (morale-positive), decline earn
//   'acceptAll'   accept everything
//   'myopic'      per-card estate heuristic: sell morale only when near the cap
//                 (M >= 85), buy morale only when it speeds study (studying, M < 80)
export function applyCard(
  st: State,
  ev: CardEvent,
  cardPolicy: CardPolicy,
  P: Params,
): void {
  let accept = false;
  if (cardPolicy === 'greedy') accept = ev.type === 'earn';
  else if (cardPolicy === 'spendthrift') accept = ev.type === 'spend';
  else if (cardPolicy === 'acceptAll') accept = true;
  else if (cardPolicy === 'myopic') {
    accept = ev.type === 'earn' ? st.M >= 85 : st.studying && st.M < 80;
  }
  if (!accept) return;
  if (ev.type === 'spend' && st.brokeLock) return; // card spends are Living (§5)
  const income = currentIncome(st, P);
  const cash = Math.min(ev.cashFrac, P.microCashCapFrac) * income;
  if (ev.type === 'earn') {
    st.S += cash;
    st.cardCashAccum += cash;
    st.M = clamp(st.M - ev.morale, 0, 100);
  } else {
    st.S -= cash;
    st.cardCashAccum -= cash;
    st.M = clamp(st.M + ev.morale, 0, 100);
  }
  st.minM = Math.min(st.minM, st.M);
  st.minS = Math.min(st.minS, st.S);
}

// --- mid-year adjustment: policy-only change (GDD §4.2) ----------------------
// Work mode and the Living dial are "changeable mid-year; all effects are
// rates". An adjustment carries NONE of the review's side effects: no
// promotion, no enrolment/study change — those belong to the annual review.
// Validation (clamping livingFrac, whitelisting mode) happens in doSubstep.
export function applyAdjustment(st: State, plan: { mode: Mode; livingFrac: number }): void {
  st.policy = { mode: plan.mode, livingFrac: plan.livingFrac };
}

// --- annual review: apply a filed plan (the single input path) ---------------
// Extracted so simulateLife (strategy-driven) and replay (log-driven) share ONE
// code path — the migration contract's "every input applied through the engine"
// (design/CONTEXT.md §3). Behaviour-preserving vs the M0 reference; the golden
// master guards that. st.J = st.K promotion is idempotent here.
export function applyPlan(st: State, plan: PlanAction, P: Params): void {
  st.J = st.K; // accept promotion at review (never refused in v1)
  if (plan.study && st.K < 6) {
    if (!st.studying) {
      const next = st.K + 1;
      if (st.enrolledSkill === next && st.p > 0) {
        st.studying = true; // resume, tuition already paid
      } else if (canEnroll(st, next, P)) {
        st.S -= P.tuition[next]!;
        st.tuitionAccum += P.tuition[next]!;
        st.enrolledSkill = next;
        st.studying = true;
        if (st.S < P.debtFloor) st.brokeLock = true;
      }
    }
  } else {
    st.studying = false; // pause: progress decays
  }
  st.policy = { mode: plan.mode, livingFrac: plan.livingFrac };
}

// --- one whole year: file plan, resolve cards, settle to year end ------------
// The shared per-year body. `year` is 0-based from startAge. Pushes exactly one
// YearRecord (matching the M0 reference's post-increment push).
export function stepYear(
  st: State,
  plan: PlanAction,
  year: number,
  P: Params,
  opts: SimOpts,
  yearlyOut: YearRecord[],
): void {
  applyPlan(st, plan, P);
  const yearEnd = P.startAge + year + 1;
  if (opts.cards) {
    const cardSeed = opts.cardSeed != null ? opts.cardSeed : 1;
    const cardPolicy: CardPolicy = opts.cardPolicy ?? 'default';
    for (const ev of cardsForYear(year, cardSeed, P)) {
      settleTo(st, P.startAge + year + ev.t, P);
      if (!st.alive) break;
      applyCard(st, ev, cardPolicy, P);
    }
  }
  settleTo(st, yearEnd, P);
  yearlyOut.push({
    age: P.startAge + year + 1,
    S: st.S,
    M: st.M,
    H: st.H,
    K: st.K,
    mode: plan.mode,
    livingFrac: plan.livingFrac,
  });
}

// --- run one life under a strategy ------------------------------------------
// strategy(st, P, year) -> { mode: 'sab'|'std'|'ot', livingFrac, study: bool }
export function simulateLife(
  strategy: Strategy,
  P: Params,
  opts: SimOpts = {},
): LifeResult {
  const st = initState(P);
  const yearly: YearRecord[] = [];
  let year = 0;

  while (st.alive && year < P.maxAge - P.startAge) {
    st.J = st.K; // promotion visible to the strategy (faithful to M0 order)
    const d = strategy(st, P, year) || {};
    const plan: PlanAction = {
      mode: d.mode || 'std',
      livingFrac: d.livingFrac || 0,
      study: !!d.study,
    };
    if (opts.record) opts.record.push({ atAge: P.startAge + year, plan });
    stepYear(st, plan, year, P, opts, yearly);
    year += 1;
  }

  const lifespan = (st.deathAge != null ? st.deathAge : ageOf(st, P)) - P.startAge;
  return {
    estate: st.S,
    deathAge: st.deathAge != null ? st.deathAge : ageOf(st, P),
    K: st.K,
    skillAges: st.skillAges,
    meanM: st.moraleInt / lifespan,
    minM: st.minM,
    flourishYears: st.flourishYears,
    minS: st.minS,
    interestAccum: st.interestAccum,
    earnedAccum: st.earnedAccum,
    colAccum: st.colAccum,
    livingAccum: st.livingAccum,
    tuitionAccum: st.tuitionAccum,
    cardCashAccum: st.cardCashAccum,
    yearly,
  };
}
